import { PRICES } from "../server/pricing";
import type { LedgerSession } from "./api";

const DAY_MS = 86_400_000;

export const HARNESS_LABEL: Record<string, string> = {
	claude: "Claude Code",
	"claude-other": "Claude Code · other models",
	grok: "Grok",
	pi: "pi",
	codex: "Codex",
	zcode: "ZCode",
	hermes: "Hermes",
	cursor: "Cursor",
};

export const BASIS_TEXT: Record<string, string> = {
	computed: "priced by us at list rates",
	recorded: "reported by the harness",
};

export function harnessLabel(harness: string): string {
	return HARNESS_LABEL[harness] ?? harness;
}

export function fmtUsd(value: number): string {
	if (value >= 1000) return `$${(value / 1000).toFixed(2)}k`;
	if (value >= 100) return `$${value.toFixed(0)}`;
	return `$${value.toFixed(2)}`;
}

export function fmtCount(value: number): string {
	const n = Math.round(value);
	if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
	return String(n);
}

/** Active minutes as `1h 42m` / `9m`; `—` when it rounds to nothing. */
export function fmtActive(min: number): string {
	const total = Math.round(min);
	if (total <= 0) return "—";
	if (total < 60) return `${total}m`;
	return `${Math.floor(total / 60)}h ${total % 60}m`;
}

/** Active minutes as hours: one decimal under 10h, whole hours above. */
export function fmtActiveHours(min: number): string {
	const hours = min / 60;
	return hours < 10 ? `${hours.toFixed(1)}h` : `${Math.round(hours)}h`;
}

function scaleMap(map: Record<string, number>, factor: number): Record<string, number> {
	const out: Record<string, number> = {};
	for (const [key, value] of Object.entries(map)) out[key] = value * factor;
	return out;
}

/**
 * Rows imported before the parsers kept a per-day map have none. Put their whole
 * cost on the UTC day they ended rather than drop them from every total.
 */
function withDays(session: LedgerSession): LedgerSession {
	const stamp = (session.last ?? session.first)?.slice(0, 10);
	if (Object.keys(session.days).length || !session.cost || !stamp) return session;
	return { ...session, days: { [stamp]: session.cost } };
}

/**
 * Pro-rate a session to the UTC days in [from, to]. Cost, tokens, and the
 * model/tool maps scale by the share of cost that falls in the window.
 * A session with no cost in the window drops out. Matches claude-ledger.
 */
export function clip(input: LedgerSession, from: string, to: string): LedgerSession | null {
	const session = withDays(input);
	const days: Record<string, number> = {};
	for (const [day, cost] of Object.entries(session.days)) {
		if (day >= from && day <= to) days[day] = cost;
	}
	const cost = Object.values(days).reduce((sum, value) => sum + value, 0);
	if (!cost) return null;
	// Active time has its own per-day map, so it clips by day rather than by the
	// cost factor — even when all the cost is in the window.
	const activeDays: Record<string, number> = {};
	for (const [day, min] of Object.entries(session.activeDays)) {
		if (day >= from && day <= to) activeDays[day] = min;
	}
	const activeMin = Object.values(activeDays).reduce((sum, value) => sum + value, 0);
	const factor = cost / (session.cost || 1);
	if (factor >= 0.999) return { ...session, activeDays, activeMin };
	return {
		...session,
		days,
		activeDays,
		activeMin,
		cost,
		modelCost: scaleMap(session.modelCost, factor),
		models: scaleMap(session.models, factor),
		tools: scaleMap(session.tools, factor),
		hours: scaleMap(session.hours, factor),
		inTok: session.inTok * factor,
		outTok: session.outTok * factor,
		cwTok: session.cwTok * factor,
		crTok: session.crTok * factor,
		thinkTok: session.thinkTok * factor,
		msgs: session.msgs * factor,
		toolCalls: session.toolCalls * factor,
		userPrompts: session.userPrompts * factor,
		durationMin: session.durationMin * factor,
		subagents: session.subagents,
	};
}

export function anchorDay(sessions: LedgerSession[], now = new Date()): string {
	const last = sessions
		.flatMap((session) => Object.keys(session.days))
		.sort()
		.pop();
	const today = now.toISOString().slice(0, 10);
	if (!last || today > last) return today;
	return last;
}

export function cutoff(anchor: string, days: number): string {
	if (!days) return "0000";
	return new Date(Date.parse(`${anchor}T00:00:00Z`) - (days - 1) * DAY_MS)
		.toISOString()
		.slice(0, 10);
}

export function selectRange(
	sessions: LedgerSession[],
	from: string,
	to: string,
	repos: Set<string>,
	harnesses: Set<string>,
): LedgerSession[] {
	const out: LedgerSession[] = [];
	for (const session of sessions) {
		if (!repos.has(session.repo) || !harnesses.has(session.harness)) continue;
		const clipped = clip(session, from, to);
		if (clipped) out.push(clipped);
	}
	return out;
}

export type HarnessSpend = {
	harness: string;
	cost: number;
	sessions: number;
	activeMin: number;
	basis: string;
};

export function spendByHarness(sessions: LedgerSession[]): HarnessSpend[] {
	const rows = new Map<string, HarnessSpend>();
	for (const session of sessions) {
		const row = rows.get(session.harness) ?? {
			harness: session.harness,
			cost: 0,
			sessions: 0,
			activeMin: 0,
			basis: session.costBasis,
		};
		row.cost += session.cost;
		row.sessions += 1;
		row.activeMin += session.activeMin;
		rows.set(session.harness, row);
	}
	return [...rows.values()].sort((a, b) => b.cost - a.cost);
}

/** Claude-only cache savings: reads bill at 0.1× input, so the saved share is ~0.9×. */
export function cacheSavings(sessions: LedgerSession[]): { saved: number; readShare: number } {
	const claude = sessions.filter((session) => session.harness === "claude");
	let saved = 0;
	let input = 0;
	let reads = 0;
	for (const session of claude) {
		const total = session.cost || 1;
		let blended = 0;
		for (const [model, cost] of Object.entries(session.modelCost)) {
			blended += (PRICES[model]?.[0] ?? 5) * (cost / total);
		}
		saved += (session.crTok / 1e6) * blended * 0.9;
		input += session.inTok + session.cwTok + session.crTok;
		reads += session.crTok;
	}
	return { saved, readShare: reads / Math.max(1, input) };
}
